/**
 * Documentation — Knowledge Base & Confluence (Epic entry page)
 *
 * Tabs:
 *   - Confluence  → Searchable wiki of in-system how-tos (ALL plans/roles)
 *   - Documents   → Downloadable PDFs + Codebase zip (SUPER ADMIN only)
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { FileText, BookOpen, ArrowLeft } from 'lucide-react';
import Layout from '@/components/Layout';
import DocumentsTab from './documents-tab/DocumentsTab';
import ConfluenceTab from './confluence-tab/ConfluenceTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Documentation() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('confluence');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (res.ok) {
          const u = await res.json();
          if (u.role === 'super_admin') {
            setIsSuperAdmin(true);
            setTab('documents');
          }
        }
      } catch (_e) { /* stays confluence-only */ }
    };
    fetchUser();
  }, []);

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="documentation-page">
        {/* Page header */}
        <div className="flex items-center gap-3 mb-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/super-admin')}
            data-testid="back-to-super-admin-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
        </div>

        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-blue-700 rounded-xl flex items-center justify-center shadow-md">
            <BookOpen className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Documentation</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Onboard your team here. Browse the wiki and learn the system.
            </p>
          </div>
        </div>

        {/* Tabs — Documents is Super Admin only */}
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList
            className={`grid w-full max-w-md ${isSuperAdmin ? 'grid-cols-2' : 'grid-cols-1'}`}
            data-testid="documentation-tabs"
          >
            {isSuperAdmin && (
              <TabsTrigger value="documents" data-testid="tab-documents">
                <FileText className="h-4 w-4 mr-2" />
                Documents
              </TabsTrigger>
            )}
            <TabsTrigger value="confluence" data-testid="tab-confluence">
              <BookOpen className="h-4 w-4 mr-2" />
              Confluence
            </TabsTrigger>
          </TabsList>

          {isSuperAdmin && (
            <TabsContent value="documents" className="mt-6">
              <DocumentsTab />
            </TabsContent>
          )}

          <TabsContent value="confluence" className="mt-6">
            <ConfluenceTab />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
